import Starling from '@ies/starling_client';
import { isOnline } from '@ad/ad_utils/lib/const';
import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { globalInfoStore } from '@/models/global';
import { I18N_FALLBACK_LANG } from '@/utils/const';

interface LocaleMessageObject {
  [key: string]: string;
}

const loadedLocales: Map<string, any> = new Map<string, any>();

// currently, only one namespace for a single project
const namespace = process.env.STARLING_NAMESPACE!;

export async function initI18Next() {
  // eslint-disable-next-line no-undef
  const defaultLng = 'en';
  // init lang from ads cookie
  try {
    await i18next
      .use(initReactI18next)
      .init({
        lng: defaultLng,
        fallbackLng: I18N_FALLBACK_LANG,
        ns: [namespace],
        defaultNS: namespace,
        debug: !isOnline,
      });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log('something went wrong loading', err);
  }
}

function getStarlingResource(locale: string) {
  return new Promise((resolve) => {
    // if in cache, return cache
    if (loadedLocales.has(locale)) {
      resolve(loadedLocales.get(locale));
      return;
    }

    const starlingRequest = new Starling({
      // eslint-disable-next-line @typescript-eslint/camelcase
      api_key: process.env.STARLING_API_KEY,
      namespace,
      test: !isOnline,
      // eslint-disable-next-line no-undef
      zoneHost: getStarlingZoneHost(ZONE as any),
      // eslint-disable-next-line no-undef
      TEAChannelDomain: getStarlingTeaChannelDomain(ZONE as any),
      fallbackLang: ['en'],
      locale,
      timeout: 10000,
    });

    starlingRequest.load(
      (text: LocaleMessageObject) => {
        loadedLocales.set(locale, text);
        resolve(text);
      },
      // if fails to load from network
      () => {
        // eslint-disable-next-line no-console
        console.warn(`failed to fetch starling resources, read from local for ${locale}`);
        // eslint-disable-next-line @typescript-eslint/no-var-requires,import/no-dynamic-require,global-require
        const obj = require(`../../${locale}.json`);
        resolve(obj);
      },
    );
  });
}

export async function loadForLang(locale: string) {
  const baseStarlingObj = await getStarlingResource(I18N_FALLBACK_LANG);
  let finalStarlingObj = baseStarlingObj;

  if (locale !== I18N_FALLBACK_LANG) {
    finalStarlingObj = await getStarlingResource(locale);

    // @ts-ignore
    finalStarlingObj = { ...baseStarlingObj, ...finalStarlingObj };
  }
  // @ts-ignore
  i18next.addResourceBundle(locale, namespace, finalStarlingObj);
}

export const localeMapping: { [name: string]: string } = {
  ar: 'ar',
  de: 'de-DE',
  'de-de': 'de-DE',
  en: 'en',
  es: 'es-ES',
  'es-es': 'es-ES',
  fr: 'fr-FR',
  'fr-fr': 'fr-FR',
  id: 'id-ID',
  'id-id': 'id-ID',
  it: 'it-IT',
  'it-it': 'it-IT',
  ja: 'ja-JP',
  jp: 'ja-JP',
  'ja-jp': 'ja-JP',
  'jp-jp': 'ja-JP',
  ko: 'ko-KR',
  kr: 'ko-KR',
  'ko-kr': 'ko-KR',
  'kr-kr': 'ko-KR',
  ms: 'ms-MY',
  my: 'ms-MY',
  'ms-my': 'ms-MY',
  'my-my': 'ms-MY',
  pt: 'pt-BR',
  'pt-br': 'pt-BR',
  ru: 'ru-RU',
  'ru-ru': 'ru-RU',
  th: 'th-TH',
  'th-th': 'th-TH',
  tr: 'tr-TR',
  'tr-tr': 'tr-TR',
  vi: 'vi-VN',
  vn: 'vi-VN',
  'vi-vn': 'vi-VN',
  'vn-vn': 'vi-VN',
  zh: 'zh',
};

export const tt4bLocaleMapping: { [name: string]: string } = {
  ar: 'ar',
  'de-DE': 'de',
  en: 'en',
  'es-ES': 'es',
  'fr-FR': 'fr',
  'id-ID': 'id',
  'it-IT': 'it',
  'ja-JP': 'ja',
  'ko-KR': 'ko',
  'ms-MY': 'ms',
  'pt-BR': 'pt-BR',
  'ru-RU': 'ru',
  'th-TH': 'th',
  'tr-TR': 'tr',
  'vi-VN': 'vi',
  zh: 'zh',
};

export const ttLocaleMapping: { [name: string]: string } = {
  ar: 'ar',
  'de-DE': 'de-DE',
  en: 'en',
  'es-ES': 'es',
  'fr-FR': 'fr',
  'id-ID': 'id-ID',
  'it-IT': 'it-IT',
  'ja-JP': 'ja-JP',
  'ko-KR': 'ko-KR',
  'ms-MY': 'ms-MY',
  'pt-BR': 'pt-BR',
  'ru-RU': 'ru-RU',
  'th-TH': 'th-TH',
  'tr-TR': 'tr-TR',
  'vi-VN': 'vi-VN',
  zh: 'zh',
};

export const TBP2SCALocaleMapping: Record<string, string> = {
  ar: I18N_FALLBACK_LANG,
  'de-DE': 'de-DE',
  en: 'en',
  'es-ES': 'es',
  'fr-FR': 'fr',
  'id-ID': 'id',
  'it-IT': 'it',
  'ja-JP': 'ja',
  'ko-KR': 'ko',
  'ms-MY': I18N_FALLBACK_LANG,
  'pt-BR': 'pt-BR',
  'ru-RU': 'ru',
  'th-TH': 'th',
  'tr-TR': 'tr',
  'vi-VN': 'vi',
  zh: 'zh-CN',
};

export const changeLng = async (locale: string) => {
  const realLocale = localeMapping[locale?.toLowerCase()] || I18N_FALLBACK_LANG;
  await loadForLang(realLocale);
  await i18next.changeLanguage(realLocale);
  globalInfoStore.lng = realLocale;
};

/*
adopted from https://code.byted.org/ad/ad_utils/blob/v11.3.3/src/utils/starling.ts
removed unused CN related code to resolve Argus blocking issues
*/
type ZONE = 'in' | 'va' | 'sg' | 'oversea' | 'cn';

export function getStarlingZoneHost(zone: ZONE) {
  const upperZone = zone.toUpperCase();

  // https://bytedance.feishu.cn/docs/doccnjYQaPo3ox06ecQl6WeeCTd
  switch (upperZone) {
    case 'SG':
      return 'https://starling-sg.tiktokv.com';
    case 'IN':
      return 'https://starling-oversea-useast2a.byteoversea.com';
    case 'VA':
      return 'https://starling-va.tiktokv.com';
    default:
      return 'https://starling-oversea.byteoversea.com';
  }
}

export function getStarlingTeaChannelDomain(zone: ZONE) {
  const upperZone = zone.toUpperCase();
  switch (upperZone) {
    case 'IN':
      return 'https://mcs-va-useast2a.tiktokv.com';
    case 'SG':
      return 'https://mcs-sg.tiktokv.com';
    default:
      return 'https://maliva-mcs.byteoversea.com';
  }
}
